import { z } from 'zod';
import { CoordsSchema } from './common';

export const SPOT_CATEGORIES = [
  'concert',
  'match',
  'restaurant',
  'bar',
  'festival',
  'college_event',
  'park',
  'mall',
  'meetup',
  'open_circle',
] as const;
export const SpotCategorySchema = z.enum(SPOT_CATEGORIES);
export type SpotCategory = z.infer<typeof SpotCategorySchema>;

export const SpotSchema = z.object({
  id: z.string(),
  name: z.string().min(2).max(80),
  category: SpotCategorySchema,
  /** Short human code to join without GPS, e.g. "K4T9". */
  code: z.string().length(4),
  lat: z.number(),
  lng: z.number(),
  geohash: z.string(),
  /** Radius in metres the Spot covers. */
  radiusM: z.number().int().positive(),
  /** Live count of checked-in people (may be 0). */
  peopleHere: z.number().int().nonnegative(),
  /** Live count of people with Glow On. */
  visibleHere: z.number().int().nonnegative(),
  approved: z.boolean(),
  /** True for auto-formed geohash-cell rooms. */
  ephemeral: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type Spot = z.infer<typeof SpotSchema>;

/* GET /api/spots/nearby?lat=..&lng=.. */
export const NearbySpotsResponseSchema = z.object({
  spots: z.array(SpotSchema),
  /** The geohash cell the caller falls in, offered as an Open Circle. */
  openCircleCell: z.string(),
});
export type NearbySpotsResponse = z.infer<typeof NearbySpotsResponseSchema>;

/* GET /api/spots/by-code/:code */
export const SpotResponseSchema = z.object({ spot: SpotSchema });
export type SpotResponse = z.infer<typeof SpotResponseSchema>;

/* POST /api/spots — user-created Spot (needs admin approval before it lists). */
export const CreateSpotSchema = z.object({
  name: z.string().min(2).max(80),
  category: SpotCategorySchema.exclude(['open_circle']),
  coords: CoordsSchema,
});
export type CreateSpot = z.infer<typeof CreateSpotSchema>;

/* POST /api/spots/:id/check-in */
export const CheckInSchema = z.object({
  coords: CoordsSchema,
  /** Optional seat/section/table aid shown on the radar. */
  section: z.string().max(24).optional(),
  appearanceBlurb: z.string().max(80).optional(),
});
export type CheckIn = z.infer<typeof CheckInSchema>;

export const CheckInResponseSchema = z.object({
  spot: SpotSchema,
});
export type CheckInResponse = z.infer<typeof CheckInResponseSchema>;

/** Reason a Spot / area is refused. */
export const SpotBlockedResponseSchema = z.object({
  blocked: z.literal(true),
  reason: z.enum(['school', 'hospital', 'worship', 'gov_military', 'other']),
  message: z.string(),
});
export type SpotBlockedResponse = z.infer<typeof SpotBlockedResponseSchema>;
